// User role / plan tiers
export type UserRole = 'FREEMIUM' | 'PREMIUM' | 'PRO' | 'ADMIN';

export const ROLE_HIERARCHY: Record<UserRole, number> = {
  FREEMIUM: 0,
  PREMIUM: 1,
  PRO: 2,
  ADMIN: 3,
};

// User types
export interface User {
  id: string;
  email: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  buyerAddress?: string;
  birthDate?: string;
  birthPlace?: string;
  createdAt: string;
  role: UserRole;
  planExpiresAt?: string | null;
}

export interface AuthResponse {
  id: string;
  email: string;
  name: string;
  token: string;
}

export interface UpdateProfileData {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  buyerAddress?: string;
  birthDate?: string;
  birthPlace?: string;
}

export interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

// User Settings types
export interface UserSettings {
  id: string;
  userId: string;
  roeTarget: number;
  mortgageRate: number;
  propertyTaxRate: number;
  amortizationRate: number;
  equityRate: number;
  maintenanceRate: number;
  acquisitionFees: number;
  equityIncludesAcquisition: boolean;
  mortgageRateStressTest: number;
  rejectionMinYield?: number | null;
  rejectionMaxPrice?: number | null;
  rejectionMinArea?: number | null;
  rejectionMinRooms?: number | null;
  rejectionMaxPriceVsMarket?: number | null;
  createdAt: string;
  updatedAt: string;
}

// InvestorProfile — modèle unifié (remplace UserSettings, même champs + nouveaux)
export interface InvestorProfile {
  id: string;
  userId: string;
  wizardStep: number;

  // Hérité de UserSettings (noms identiques)
  roeTarget: number;
  mortgageRate: number;
  mortgageRateStressTest: number;
  propertyTaxRate: number;
  amortizationRate: number;
  maintenanceRate: number;
  equityRate: number;
  acquisitionFees: number;
  equityIncludesAcquisition: boolean;
  rejectionMinYield?: number | null;
  rejectionMaxPrice?: number | null;
  rejectionMinArea?: number | null;
  rejectionMinRooms?: number | null;
  rejectionMaxPriceVsMarket?: number | null;

  // Objectifs
  objectifPrincipal?: 'CASHFLOW' | 'CAPITAL' | 'MIXTE' | 'SECURITE' | null;
  horizon?: 'COURT' | 'MOYEN' | 'LONG' | null;
  roeCible?: number | null;
  cashflowCible?: number | null;
  nbBiensCible?: number | null;

  // Stratégie
  strategies?: string[] | null;
  toleranceRisque?: number | null;
  accepteTravaux: boolean;
  accepteVacance: boolean;

  // Paramètres flip
  flipFacteurVente?: number | null;
  flipBudgetCuisine?: number | null;
  flipBudgetSalleBain?: number | null;
  flipCoutMenuiserie?: number | null;
  flipCoutSol?: number | null;
  flipCoutElectricite?: number | null;
  flipMargeVoulue?: number | null;
  flipFraisAchat?: number | null;
  flipFraisRevente?: number | null;
  flipFraisEmprunt?: number | null;

  // Moyens financiers
  fondsPropresCHF?: number | null;
  capaciteEmprunt?: number | null;
  budgetRenovMax?: number | null;
  projetSimultanes: number;

  // Critères bien
  localisation?: { cantons?: string[]; villes?: string[]; rayonKm?: number } | null;

  // Critères financiers
  cashflowMin?: number | null;

  // Notifications
  notifFrequence: 'NEVER' | 'IMMEDIATE' | 'DAILY' | 'WEEKLY';
  notifTypeOpportunite: 'OPPORTUNITY' | 'INTERESTING' | 'ALL';
  scoreMin: number;
  notifFormatEmail: 'DIGEST' | 'DETAIL';
  alertFlipNiveau: 'GREEN' | 'GREEN_ORANGE';

  createdAt: string;
  updatedAt: string;
}

export interface RejectionCriteriaData {
  rejectionMinYield: string;
  rejectionMaxPrice: string;
  rejectionMinArea: string;
  rejectionMinRooms: string;
  rejectionMaxPriceVsMarket: string;
}

export interface OpportunityCriteriaData {
  roeTarget: string;
  mortgageRate: string;
  mortgageRateStressTest: string;
  propertyTaxRate: string;
  amortizationRate: string;
  equityRate: string;
  maintenanceRate: string;
  acquisitionFees: string;
  equityIncludesAcquisition: boolean;
}

// Property types
export enum PropertyStatus {
  FIRST_SELECTION = 'FIRST_SELECTION',
  OPPORTUNITY = 'OPPORTUNITY',
  UNDER_ANALYSIS = 'UNDER_ANALYSIS',
  PURCHASED = 'PURCHASED',
  WITHDRAWN = 'WITHDRAWN',
  REJECTED = 'REJECTED',
}

export const PROPERTY_STATUS_LABELS: Record<PropertyStatus, string> = {
  [PropertyStatus.FIRST_SELECTION]: 'Première sélection',
  [PropertyStatus.OPPORTUNITY]: 'Opportunité',
  [PropertyStatus.UNDER_ANALYSIS]: 'Analyse en cours',
  [PropertyStatus.PURCHASED]: 'Achetée',
  [PropertyStatus.WITHDRAWN]: 'Retiré',
  [PropertyStatus.REJECTED]: 'Rejetée',
};

export interface Commune {
  id: string;
  bfsNr: number;
  npa: string;
  name: string;
  canton: string;
  population?: number | null;
  populationPrev?: number | null;
  populationYear?: number | null;
  unemploymentRate?: number | null;
  unemploymentRatePrev?: number | null;
  unemploymentPeriod?: string | null;
  incomeMedian?: number | null;
  incomeMedianPrev?: number | null;
  incomeYear?: number | null;
  updatedAt: string;
}

export interface Property {
  id: string;
  userId: string;

  // Status et propriété
  status: PropertyStatus;
  isOwned: boolean;

  // Import URL / Crawlers
  sourceUrl?: string;
  sourceName?: string;
  scrapedAt?: string;
  agencyName?: string;
  agencyContactPerson?: string;
  agencyAddress?: string;
  agencyPhone?: string;
  agencyEmail?: string;

  // Champs calculés pour opportunités
  yieldRate?: number;
  pricePerM2?: number;
  cashFlowMonthly?: number;

  // Estimation loyer automatique
  estimatedRent?: number;
  estimatedYield?: number;
  rentConfidence?: 'high' | 'medium' | 'low';

  // Estimation prix de vente marché (RealAdvisor ou calculateur local)
  estimatedSalePrice?: number;
  estimatedSalePriceMin?: number;
  estimatedSalePriceMax?: number;
  saleEstimateSource?: 'realadvisor' | 'local';
  marketPricePerM2?: number | null;

  // Tracking présence sur le marché
  firstSeenAt?: string;
  lastSeenAt?: string;
  offMarketAt?: string | null;
  isOffMarket?: boolean;
  priceHistory?: Array<{ price: number; seenAt: string }>;

  // ROE calculation fields
  downPayment?: number;
  calculatedRoe?: number;

  // Informations générales
  strategy: string;
  affectation: string;
  floorsInBuilding: number;
  floor: string;
  rooms: number;
  bathrooms: number;
  hasGuestToilet: boolean;
  hasElevator: boolean;
  hasCellar: boolean;
  hasAttic: boolean;

  // Localisation
  address: string;
  city: string;
  postalCode: string;
  canton?: string;
  country: string;
  latitude?: number;
  longitude?: number;
  egid?: number;

  // Surfaces
  area: number;
  weightedArea?: number;
  exteriorArea?: number;
  exteriorType?: string;
  landArea?: number;

  // État et rénovation
  buildingYear?: number;
  buildingRenovationYear?: number;
  apartmentRenovationYear?: number;
  buildingCondition?: string;
  apartmentCondition?: string;
  heatingType?: string;
  insulation?: string;

  // État de l'immeuble - Détails
  buildingGeneralAspect?: string;
  buildingStructure?: string;
  buildingRoof?: string;
  buildingFacades?: string;
  buildingWindows?: string;
  buildingCorridors?: string;
  buildingTechnicalInstall?: string;
  hasCEBC?: boolean;
  hasMinergie?: boolean;

  // État de l'appartement - Détails
  condPlastering?: string;
  condParquet?: string;
  condTiling?: string;
  condElectricity?: string;
  condPlumbing?: string;
  condJoinery?: string;
  condKitchen?: string;

  // Parking
  coveredParkingCount: number;
  uncoveredParkingCount: number;
  hasIndividualGarage: boolean;

  // Finances
  askingPrice?: number;
  ppeCharges?: number;
  renovationFund?: number;
  buildingInsurance?: number;
  isRented: boolean;
  rentPrice?: number;
  rentIncludesCharges: boolean;
  tenantCharges?: number;

  estimatedPrice?: number;
  description?: string;

  // Analyse automatique de description (LLM)
  hasWorks?: boolean | null;
  isMixedUse?: boolean | null;
  isAddressComplete?: boolean | null;
  descriptionAnalyzedAt?: string | null;
  isStrategyLocatif?: boolean | null;
  isStrategyFlip?: boolean | null;

  // Analyse photos (llava:7b)
  photoRenovationLevel?: 'none' | 'minor' | 'major' | null;
  photoAnalyzedAt?: string | null;

  // Signal unifié rénovation (hasWorks + photoRenovationLevel) — calculé backend
  renovationLevel?: 'none' | 'minor' | 'major' | null;

  // Score opportunité (calculé par ScoringService, stocké dans PropertySelection)
  opportunityScore?: number | null;
  scoreDetails?: { financial: number; bien: number; strategie: number; comportemental: number } | null;
  flipPrixMarcheM2?: number | null;
  flipFacteurVente?: number | null;
  flipBudgetCuisine?: number | null;
  flipBudgetSalleBain?: number | null;
  flipCoutMenuiserie?: number | null;
  flipCoutSol?: number | null;
  flipCoutElectricite?: number | null;
  flipMargeVoulue?: number | null;
  flipFraisAchat?: number | null;
  flipFraisRevente?: number | null;
  flipFraisEmprunt?: number | null;

  // Données environnementales GeoAdmin
  unitsInBuilding?: number | null;
  floodHazard?: string | null;
  roadNoiseDb?: number | null;
  railNoiseDb?: number | null;

  photos: UploadedFile[];
  commune?: Commune | null;
  createdAt: string;
  updatedAt: string;
}

export interface PropertyFormData {
  // Informations générales
  address: string;
  postalCode: string;
  city: string;
  strategy: string;
  affectation: string;
  status: PropertyStatus;

  // Caractéristiques
  floorsInBuilding: number;
  floor: string;
  rooms: number;
  bathrooms: number;
  hasGuestToilet: boolean;
  hasElevator: boolean;
  hasCellar: boolean;
  hasAttic: boolean;

  // Surfaces
  area: number;
  weightedArea?: number;
  exteriorArea?: number;
  exteriorType?: string;

  // État
  buildingYear?: number;
  buildingRenovationYear?: number;
  apartmentRenovationYear?: number;
  buildingCondition?: string;
  apartmentCondition?: string;

  // État de l'immeuble - Détails
  buildingGeneralAspect?: string;
  buildingStructure?: string;
  buildingRoof?: string;
  buildingFacades?: string;
  buildingWindows?: string;
  buildingCorridors?: string;
  buildingTechnicalInstall?: string;
  hasCEBC?: boolean;
  hasMinergie?: boolean;

  // État de l'appartement - Détails
  condPlastering?: string;
  condParquet?: string;
  condTiling?: string;
  condElectricity?: string;
  condPlumbing?: string;
  condJoinery?: string;
  condKitchen?: string;

  // Parking
  coveredParkingCount: number;
  uncoveredParkingCount: number;
  hasIndividualGarage: boolean;

  // Finances
  askingPrice?: number;
  ppeCharges?: number;
  renovationFund?: number;
  buildingInsurance?: number;
  isRented: boolean;
  rentPrice?: number;
  rentIncludesCharges: boolean;
  tenantCharges?: number;

  // ROE calculation field
  downPayment?: number;

  // Autres champs optionnels
  country?: string;
  latitude?: number;
  longitude?: number;
  egid?: number;
  landArea?: number;
  heatingType?: string;
  insulation?: string;
  description?: string;
}

// File types
export interface UploadedFile {
  id: string;
  propertyId: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  url: string;
  isPrimary: boolean;
  createdAt: string;
}

// Estimation types
export interface Estimation {
  id: string;
  userId: string;
  propertyId: string;
  estimatedPrice: number;
  minPrice: number;
  maxPrice: number;
  confidence: number;
  factors: Record<string, any>;
  algorithm: string;
  llmAnalysis?: string;
  createdAt: string;
  updatedAt: string;
}

// Property Analysis types
export interface AnalysisSettings {
  roeTarget: number;
  mortgageRate: number;
  mortgageRateStressTest: number;
  propertyTaxRate: number;
  amortizationRate: number;
  maintenanceRate: number;
  equityRate: number;
  acquisitionFees: number;
  equityIncludesAcquisition: boolean;
}

export interface AnalysisData {
  loyerAnnuel: number;
  chargesPPEAnnuelles: number;
  coutsMaintenance: number;
  impotFoncier: number;
  fraisAcquisition: number;
  prixTotal: number;
  apport: number;
  pretHypothecaire: number;
  fondsPropreNecessaire: number;
  chargesInteret: number;
  amortissement: number;
  rendementBrut: number;
  rendementNet: number;
  cashFlowAnnuel: number;
  cashFlowMensuel: number;
  roe: number;
  tauxCapitalisationDefaut: number;
  offreConseillee: number;
  prixM2: number;
  prixM2Conseille: number;
  etatGeneral: number;
  emplacementScore: number;
  chargesInteretStressTest: number;
  cashFlowAnnuelStressTest: number;
  cashFlowMensuelStressTest: number;
  roeStressTest: number;
}

// Valeurs recalculées pour l'offre conseillée (pilotées par le slider de taux de capitalisation)
export interface OffreData {
  offreConseillee: number;
  prixM2Conseille: number;
  fraisAcquisition: number;
  prixTotal: number;
  pretHypothecaire: number;
  fondsPropreNecessaire: number;
  chargesInteret: number;
  amortissement: number;
  coutsMaintenance: number;
  impotFoncier: number;
  rendementBrut: number;
  rendementNet: number;
  cashFlowAnnuel: number;
  cashFlowMensuel: number;
  roe: number;
}

export interface PropertyAnalysis {
  property: Property;
  settings: AnalysisSettings;
  analysis: AnalysisData;
}

// Property Notes
export interface PropertyNote {
  id: string;
  propertyId: string;
  content: string;
  createdAt: string;
  updatedAt: string;
}

// PPE Document types
export type PpeDocumentStatus = 'UPLOADED' | 'EXTRACTING' | 'DONE' | 'ERROR';

export interface PpeDocument {
  id: string;
  propertyId: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  status: PpeDocumentStatus;
  errorMessage: string | null;
  createdAt: string;
}

export interface PpeAnomaly {
  type: string;
  description: string;
  severity: 'low' | 'medium' | 'high';
  years: number[];
}

export interface PpeReport {
  id: string;
  propertyId: string;
  financeScore: number | null;
  financeComment: string | null;
  travauxScore: number | null;
  travauxComment: string | null;
  litigesScore: number | null;
  litigesComment: string | null;
  globalScore: number | null;
  globalComment: string | null;
  analysisData: any;
  yearsAnalyzed: number[];
  anomalies: PpeAnomaly[] | null;
  lastAnalyzedAt: string | null;
}

// RAG Analysis - Standardized Indicators
export interface RagIndicator {
  name: string;
  label: string;
  value?: number | string | null;
  unit?: string;
  score: number; // 1-10
  trend?: 'stable' | 'hausse' | 'baisse';
  comment: string;
}

export interface RagIndicatorFamily {
  family: string;
  label: string;
  familyScore: number;
  indicators: RagIndicator[];
  summary: string;
}

export interface RagRisk {
  description: string;
  severity: 'faible' | 'moyen' | 'eleve' | 'critique';
  impact: number;
}

export interface RagOpportunity {
  description: string;
  potential: 'faible' | 'moyen' | 'eleve';
}

export interface StandardizedAnalysis {
  propertyId: string;
  globalScore: number;
  families: RagIndicatorFamily[];
  risks: RagRisk[];
  opportunities: RagOpportunity[];
  analyzedAt: string;
  llmModel: string;
}

// RAG Analysis Progress
export interface AnalysisStatus {
  status: 'idle' | 'running' | 'done' | 'error';
  phase?: 'extracting' | 'embedding' | 'analyzing' | '';
  currentDoc?: string;
  docsTotal?: number;
  docsDone?: number;
  currentFamily: string;
  familiesDone: string[];
  progress: number; // 0-10
  error?: string;
}

// Analysis history
export interface AnalysisRun {
  id: string;
  propertyId: string;
  status: 'RUNNING' | 'DONE' | 'ERROR';
  startedAt: string;
  completedAt?: string;
  analysisData?: StandardizedAnalysis;
  globalScore?: number;
  llmModel?: string;
  docsUsed: number;
  docsErrored: number;
  errorMessage?: string;
}

// API Response wrapper
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  timestamp: string;
}

// Pagination
export interface PaginatedResponse<T> {
  properties: T[];
  total: number;
  skip: number;
  take: number;
}

// Property filters
export interface PropertyFilters {
  status?: PropertyStatus;
  isOwned?: boolean;
  isOffMarket?: boolean;
  skip?: number;
  take?: number;
}

// Import URL
export interface ImportUrlRequest {
  url: string;
}

// ─────────────────────────────────────────────
// Portfolio
// ─────────────────────────────────────────────

export interface PortfolioIndicators {
  effectiveRent: number;
  totalCharges: number;
  NOI: number;
  annualInterest: number;
  cashFlow: number;
  cashFlowMonthly: number;
  LTV: number | null;
  capRate: number | null;
  netYield: number | null;
  grossYield: number | null;
  equity: number | null;
}

export interface PortfolioProperty {
  id: string;
  userId: string;
  propertyId?: string | null;

  // Général
  name: string;
  propertyType: string;
  address?: string | null;
  city?: string | null;
  postalCode?: string | null;
  canton?: string | null;
  acquisitionDate?: string | null;
  acquisitionPrice?: number | null;
  area?: number | null;
  rooms?: number | null;
  notes?: string | null;

  // Revenus
  grossAnnualRent?: number | null;
  vacancyRate: number;

  // Charges
  ppeChargesAnnual?: number | null;
  maintenanceCosts?: number | null;
  propertyTax?: number | null;
  otherCosts?: number | null;

  // Financement
  currentDebt?: number | null;
  interestRate?: number | null;
  rateType?: string | null;
  annualAmortization?: number | null;

  // Valorisation
  currentMarketValue?: number | null;
  valuationMethod?: string | null;

  createdAt: string;
  updatedAt: string;

  // Calculés par le backend
  indicators: PortfolioIndicators;
}

export interface PortfolioSummary {
  propertyCount: number;
  totalMarketValue: number;
  totalDebt: number;
  totalEquity: number;
  totalNOI: number;
  totalCashFlow: number;
  globalLTV: number | null;
  avgCapRate: number | null;
}

export interface PortfolioListResponse {
  properties: PortfolioProperty[];
  summary: PortfolioSummary;
}

export interface PortfolioPropertyFormData {
  name: string;
  propertyId?: string;
  propertyType?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  canton?: string;
  acquisitionDate?: string;
  acquisitionPrice?: number;
  area?: number;
  rooms?: number;
  notes?: string;
  grossAnnualRent?: number;
  vacancyRate?: number;
  ppeChargesAnnual?: number;
  maintenanceCosts?: number;
  propertyTax?: number;
  otherCosts?: number;
  currentDebt?: number;
  interestRate?: number;
  rateType?: string;
  annualAmortization?: number;
  currentMarketValue?: number;
  valuationMethod?: string;
}

// ─── Simulation types ────────────────────────────────────────────────────────

export interface SimulationParameters {
  purchasePrice: number;
  acquisitionCosts?: number;       // frais annexes CHF
  downPaymentPct: number;          // apport propre %
  interestRate: number;            // taux %
  annualAmortizationPct?: number;  // amortissement % du prêt / an (défaut 1%)
  grossAnnualRent?: number;        // loyer brut annuel CHF
  vacancyRate?: number;            // taux de vacance %
  annualCharges?: number;          // charges totales annuelles CHF
}

export interface SimulationResults {
  loanAmount: number;
  equityRequired: number;
  effectiveRent: number;
  NOI: number;
  annualInterest: number;
  annualAmortization: number;
  cashFlow: number;
  cashFlowMonthly: number;
  LTV: number;
  capRate: number | null;
  netYield: number | null;
  cashOnCash: number | null;
}

export interface PortfolioSimulation {
  id: string;
  name: string;
  type: string;
  portfolioPropertyId?: string | null;
  propertyId?: string | null;
  parameters: SimulationParameters;
  results: SimulationResults;
  createdAt: string;
}

export interface PropertyRef {
  id: string;
  address: string | null;
  city: string;
  postalCode: string | null;
  askingPrice: number | null;
}

export interface EnrichedSimulation extends PortfolioSimulation {
  property: PropertyRef | null;
}

// Address autocomplete types
export interface AddressSuggestion {
  label: string;          // "Rue En Galilée 1, 2022 Bevaix"
  address: string;        // "Rue En Galilée 1"
  postalCode: string;     // "2022"
  city: string;           // "Bevaix"
  egid: number;           // 123456
  latitude: number;       // 46.9339
  longitude: number;      // 6.8242
}

export interface BuildingData {
  egid: number;
  buildingYear?: number;
  heatingType?: string;
  heatingEnergy?: string;
  floorsInBuilding?: number;
  buildingCondition?: string;
  renovationYear?: number;
}

// RegBL / GWR Building Info (live from GeoAdmin API)
export interface RegblData {
  egid: number;
  coordE?: number;
  coordN?: number;
  plotNumber?: string;
  buildingCategory?: string;
  buildingClass?: string;
  constructionPeriod?: string;
  numberOfFloors?: number;
  buildingArea?: number;
  energyReferenceArea?: number;
  buildingVolume?: number;
  heatingGenerator?: string;
  heatingEnergy?: string;
  heatingDate?: string;
  hotWaterGenerator?: string;
  hotWaterEnergy?: string;
  hotWaterDate?: string;
  unitsInBuilding?: number;
  floodHazard?: string;
  roadNoiseDb?: number;
  railNoiseDb?: number;
}

// POI - Points of Interest (Localisation)
export interface PoiResult {
  category: string;
  label: string;
  name: string;
  distance: number; // meters
  lat: number;
  lng: number;
}

export interface PoiData {
  lat: number;
  lng: number;
  pois: PoiResult[];
  fetchedAt: string;
}

export interface MarketStats {
  totalTracked: number;
  totalActive: number;
  totalOffMarket: number;
  avgDaysOnMarket: number | null;
  ageDistribution: {
    lt14: number;
    lt60: number;
    lt180: number;
    gt180: number;
  };
  byCity: Array<{
    city: string;
    total: number;
    offMarket: number;
    offMarketRate: number;
  }>;
  priceDrops: Array<{
    id: string;
    city: string | null;
    originalPrice: number;
    currentPrice: number;
    dropPct: number;
  }>;
}
